import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import moment from "moment";

import { FILE_UTILS } from "utils/files";
import { LoadingOrContent } from "@/components";
import { Welcome, OptOutForm, OptInForm } from "./components";
import { NAVIGATION_STATES } from "./constants";

import logoNoBg from "@/assets/logo-no-bg.png";
import { useQuery } from "@/hooks";
import { useOptNavigation, useGetJobBoardByJobID, useSaveJobBoardByJobID } from "./hooks";

import styles from "./Opt.module.scss";

const Opt = () => {
  const { jobID } = useParams();
  const query = useQuery();

  const [base64Resume, setBase64Resume] = useState(null);
  const [optedIn, setOptedIn] = useState(null);
  const submissionID = query.get("submission");

  /**
   * Opt Navigation.
   */
  const {
    handleError,
    handleFilledOut,
    handleSubmitted,
    handleOptIn,
    handleOptOut,
    handleWelcome,
    navigationState
  } = useOptNavigation();

  /**
   * Get Opt Data.
   */
  const {
    jobBoard: currentJob,
    isLoading: isGettingJobBoard,
    jobBoardError
  } = useGetJobBoardByJobID(jobID, submissionID);

  useEffect(() => {
    const noCandy = currentJob !== null && !currentJob?.candidate;
    const hasError = jobBoardError?.status > 200;

    if (noCandy || hasError) {
      handleError();
    }
  }, [currentJob, jobBoardError]);

  /**
   * Show Thanks Screen for filled out forms.
   */
  useEffect(() => {
    if (currentJob?.isFilledOut) {
      handleFilledOut();
    }
  }, [currentJob?.isFilledOut]);

  const {
    saveJobBoard,
    isLoading: isSavingJobBoard,
    response: saveResponse
  } = useSaveJobBoardByJobID(jobID);

  useEffect(() => {
    if (saveResponse?.status === 200) {
      handleSubmitted();
    }
  }, [saveResponse]);

  const submission = {
    id: submissionID,
    candidate: currentJob?.candidate
  };

  const varinerContact = {
    name: `${currentJob?.candidate?.owner?.firstName} ${currentJob?.candidate?.owner?.lastName}`,
    email: currentJob?.candidate?.owner?.email
  };

  /**
   * Opt Out Submit.
   */
  const handleOptOutSubmit = (values) => {
    const data = {
      status: values.optOutReason,
      comments: values.optOutComments
    };

    const payload = {
      id: submission.id,
      jobID,
      candidateID: currentJob.candidate.id,
      data
    };

    setOptedIn(false);
    saveJobBoard(payload);
  };

  /**
   * Opt In Submit.
   */
  const handleOptInSubmit = (values) => {
    const formattedDate = moment().format("M_D_YY");

    const data = {
      status: "Confirmed Interest",
      customTextBlock1: values.roleInterest,
      customTextBlock2: values.locationInterest,
      customDate2: moment(values.earliestStartDate).valueOf(),
      customDate1: moment(values.idealStartDate).valueOf(),
      customTextBlock3: values.relevantExperience
    };

    const payload = {
      id: submission.id,
      jobID,
      candidateID: currentJob.candidate.id,
      data,
      resumeContent: base64Resume,
      responses: data,
      jobTitle: currentJob.publicName,
      clientCorporation: {
        name: currentJob.clientCorporation.name
      },
      fileName: `${submission?.candidate?.firstName}${submission?.candidate?.lastName}_CV-job_${jobID}-${submission.id}-${formattedDate}.pdf`
    };

    setOptedIn(true);
    saveJobBoard(payload);
  };

  const handleFileChange = async (files) => {
    if (files && files[0]) {
      const transformedFile = await FILE_UTILS.transformBase64(files[0]);
      setBase64Resume(transformedFile);
    } else {
      setBase64Resume(null);
    }
  };

  return (
    <div className={styles.layout}>
      <div className={styles.container}>
        <div className={styles.container__logo}>
          <a href="https://www.variner.com">
            <img src={logoNoBg} style={{ maxHeight: 15 }} alt="variner logo" />
          </a>
        </div>

        <div className={styles.container__content}>
          <LoadingOrContent isLoading={isGettingJobBoard}>
            {/* @TODO create error component. */}
            {navigationState === NAVIGATION_STATES.ERROR && (
              <div className={styles["thanks-wrapper"]}>
                <h1 className={styles.title}>something fails</h1>
                <p className={styles.text}>
                  Sorry, something seems to be wrong with this URL. If you believe this notice is in
                  error, please reach out to your contact at Variner.
                </p>
              </div>
            )}
            {navigationState === NAVIGATION_STATES.SUBMITTED && (
              <div className={styles["thanks-wrapper"]}>
                <h1 className={styles.title}>Thank you!</h1>
                <p className={styles.text}>
                  {optedIn
                    ? `We're excited to include you in this search process. We will be in touch as soon as we know more from ${currentJob?.clientCorporation?.name}.`
                    : "We sincerely appreciate you taking the time to consider this opportunity. Please keep in touch."}
                </p>
              </div>
            )}
            {navigationState === NAVIGATION_STATES.FILLED_OUT && (
              <div className={styles["thanks-wrapper"]}>
                <h1 className={styles.title}>Thank you!</h1>
                <p className={styles.text}>
                  It looks like we&apos;ve already received your opt-in responses for{" "}
                  <strong>{currentJob.publicName}</strong>. If you have questions please reach out
                  to <a href={`mailto:${varinerContact.email}`}>{varinerContact.name}</a>
                </p>
              </div>
            )}

            {navigationState === NAVIGATION_STATES.WELCOME && (
              <Welcome
                currentJob={currentJob}
                submission={submission}
                varinerContact={varinerContact}
                handleOptIn={handleOptIn}
                handleOptOut={handleOptOut}
              />
            )}

            <LoadingOrContent isLoading={isSavingJobBoard}>
              {navigationState === NAVIGATION_STATES.OPT_OUT && (
                <OptOutForm
                  currentJob={currentJob}
                  handleBack={handleWelcome}
                  handleSubmit={handleOptOutSubmit}
                />
              )}

              {navigationState === NAVIGATION_STATES.OPT_IN && (
                <OptInForm
                  currentJob={currentJob}
                  handleBack={handleWelcome}
                  handleSubmit={handleOptInSubmit}
                  handleFileChange={handleFileChange}
                />
              )}
            </LoadingOrContent>
          </LoadingOrContent>
        </div>
      </div>

      <div className={styles.mountains}></div>
    </div>
  );
};

export { Opt };
